import { useRef } from 'react';
import { Autocomplete, MenuItem, Stack, TextField, Typography } from '@mui/material';

import Locale, { editorLocales } from 'Locale';
import LocalePackGroupsField from './LocalePackGroupsField';
import OpenInNewTabButton from './OpenInNewTabButton';
import RemoveButton from './RemoveButton';
import { renderOptionKeyedById } from './renderOptionKeyedById';
import { titleWithTag } from './titleWithTag';
import {
    canChangeLocale,
    englishTranslation,
    localePackForLocaleChange,
    setContentItemForLocale,
} from './localePackHelpers';
import {
    type EditableLocalePack,
    type LinkableContentItem,
    type LocalePackContentItem,
} from './ContentItemEditor.types';

// The Angular template listed these labels in English rather than reading Locale.title, which is
// each language's own name. This is an editor-only screen, so the English names stay.
const LOCALE_LABELS: Record<string, string> = {
    en: 'English',
    es: 'Spanish',
    it: 'Italian',
    zh: 'Chinese',
    ar: 'Arabic',
    am: 'Amharic',
};

type Props<Pack extends EditableLocalePack> = {
    itemId: string;
    itemTitle: string;

    // 'course' or 'playlist', lower case.
    itemLabel: string;
    locale: string;
    localePack: Pack | null | undefined;

    // Every item of this kind that could be linked as a translation. The caller loads the list.
    linkableItems: LinkableContentItem[];
    loading: boolean;

    // True for a kind of content item whose locale pack carries access groups. Courses do, playlists
    // and lessons do not.
    groupable: boolean;
    editorUrlFor: (id: string) => string;
    onChange: (change: { locale: string; localePack: Pack | null }) => void;
};

function TranslationPicker({
    locale,
    label,
    options,
    selected,
    editorUrlFor,
    onSelect,
}: {
    locale: string;
    label: string;
    options: LinkableContentItem[];
    selected: LocalePackContentItem | undefined;
    editorUrlFor: (id: string) => string;
    onSelect: (item: LinkableContentItem | null) => void;
}) {
    const value = options.find(option => option.id === selected?.id) || null;

    return (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Autocomplete
                fullWidth
                value={value}
                options={options}
                disabled={options.length === 0}
                getOptionLabel={titleWithTag}
                isOptionEqualToValue={(option, chosen) => option.id === chosen.id}
                onChange={(_event, option) => onSelect(option)}
                renderOption={renderOptionKeyedById(titleWithTag)}
                renderInput={props => <TextField {...props} label={label} name={`linked_content_item_${locale}`} />}
            />

            {!!selected && <OpenInNewTabButton href={editorUrlFor(selected.id)} label={`Open the ${label} editor`} />}
        </Stack>
    );
}

/*
    The React version of the Angular content-item-edit-locale directive, for the course editor. The
    Angular directive stays where it is for the lesson translation tools.

    An English item owns the links to its translations, so only an English item shows the pickers. A
    translation shows which English item it belongs to and can detach itself.
*/
// oxlint-disable-next-line max-lines-per-function -- one field group with two mutually exclusive halves
export default function EditContentItemLocale<Pack extends EditableLocalePack>({
    itemId,
    itemTitle,
    itemLabel,
    locale,
    localePack,
    linkableItems,
    loading,
    groupable,
    editorUrlFor,
    onChange,
}: Props<Pack>) {
    /*
        The English pack, so switching to a translation locale and back restores the links rather
        than losing them. Only ever an English one: seeded with the pack of a translation, unlinking
        that translation and then choosing English would hand back the pack it shares with the
        English course, and the save re-attaches every item in it. Two courses of one locale would
        then sit in one pack.
    */
    const originalPackRef = useRef(locale === 'en' ? localePack : null);
    if (locale === 'en' && localePack) originalPackRef.current = localePack;

    const item = { id: itemId, title: itemTitle, locale };
    const linkedEnglishItem = englishTranslation(item, localePack);

    function handleLocaleChange(newLocale: string) {
        onChange({
            locale: newLocale,
            localePack: localePackForLocaleChange({
                item: { ...item, locale: newLocale },
                newLocale,
                originalPack: originalPackRef.current,
            }) as Pack | null,
        });
    }

    function handleLink(forLocale: string, linked: LinkableContentItem | null) {
        if (!localePack) return;

        onChange({
            locale,
            localePack: setContentItemForLocale(
                localePack,
                forLocale,
                linked ? { id: linked.id, title: linked.title, locale: forLocale } : null,
            ),
        });
    }

    return (
        <Stack spacing={2}>
            <TextField
                select
                fullWidth
                name="locale"
                label="Locale"
                value={locale || 'en'}
                disabled={!canChangeLocale(localePack)}
                onChange={event => handleLocaleChange(event.target.value)}
                helperText={
                    canChangeLocale(localePack) ? undefined : 'Unlink the translations before you change the locale.'
                }
            >
                {editorLocales.map(option => (
                    <MenuItem key={option.code} value={option.code}>
                        {LOCALE_LABELS[option.code] || option.title}
                    </MenuItem>
                ))}
            </TextField>

            {locale === 'en' &&
                !loading &&
                Locale.editorLocalesWithoutEnglish.map(option => (
                    <TranslationPicker
                        key={option.code}
                        locale={option.code}
                        label={`${LOCALE_LABELS[option.code] || option.title} version`}
                        // A course can only link to one that is unattached or already in this pack,
                        // and never to itself.
                        options={linkableItems.filter(
                            linkable =>
                                linkable.locale === option.code &&
                                linkable.id !== itemId &&
                                (!linkable.localePackId || linkable.localePackId === localePack?.id),
                        )}
                        selected={(localePack?.content_items || []).find(
                            contentItem => contentItem.locale === option.code,
                        )}
                        editorUrlFor={editorUrlFor}
                        onSelect={linked => handleLink(option.code, linked)}
                    />
                ))}

            {!!linkedEnglishItem && (
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Typography>{`Translation of "${linkedEnglishItem.title}"`}</Typography>
                    <OpenInNewTabButton
                        href={editorUrlFor(linkedEnglishItem.id)}
                        label={`Open the English ${itemLabel} editor`}
                    />

                    {/* Detaches this translation from its pack, as the Angular remove button did. */}
                    <RemoveButton
                        name="unlink_translation"
                        label="Unlink this translation"
                        onClick={() => onChange({ locale, localePack: null })}
                    />
                </Stack>
            )}

            {groupable && locale === 'en' && !!localePack && (
                <LocalePackGroupsField
                    value={localePack.groups || []}
                    onChange={groups => onChange({ locale, localePack: { ...localePack, groups } })}
                />
            )}
        </Stack>
    );
}
